import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { MetricCard } from "@/components/metrics/MetricCard";
import { CaseStudyTOC } from "@/components/caseStudy/CaseStudyTOC";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { getCaseStudy, CASE_STUDIES, type CaseStudy } from "@/content/caseStudies";
import { pageMeta, ldScripts, breadcrumbLd, articleLd } from "@/lib/seo";
import { Picture } from "@/components/media/Picture";

export const Route = createFileRoute("/case-studies/$slug")({
  loader: ({ params }) => {
    const study = getCaseStudy(params.slug);
    if (!study) throw notFound();
    return { study };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Case study not found — Optimize Plus" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const s = loaderData.study;
    const path = `/case-studies/${params.slug}/`;
    const title = `${s.hero.headline} — Optimize Plus Case Study`;
    const base = pageMeta({
      path,
      title,
      description: s.hero.deck,
      image: s.project.cover,
      type: "article",
      keywords: [
        s.project.client,
        s.project.industry,
        `${s.project.industry} SEO case study`,
        `${s.project.industry} SEO Austin`,
        "SEO case study Texas",
        "Austin SEO results",
        "Optimize Plus",
      ],
    });
    return {
      ...base,
      scripts: ldScripts(
        articleLd({
          headline: title,
          description: s.hero.deck,
          path,
          datePublished: "2024-01-01",
          image: s.project.cover,
        }),
        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Case Studies", path: "/case-studies" },
          { name: s.project.client, path },
        ]),
      ),
    };
  },
  component: CaseStudyDetail,
  notFoundComponent: () => (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1} className="container-page py-32 text-center">
        <h1 className="font-display text-4xl">Case study not found</h1>
        <Link to="/case-studies/" className="mt-6 inline-block text-primary">
          ← All case studies
        </Link>
      </main>
    </div>
  ),
});

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function CaseStudyDetail() {
  const { study } = Route.useLoaderData() as { study: CaseStudy };
  const more = CASE_STUDIES.filter((c) => c.slug !== study.slug).slice(0, 2);

  const tocItems = [
    { id: "summary", label: "Executive summary" },
    { id: "results", label: "Measured results" },
    ...study.sections.map((s) => ({ id: slugify(s.heading), label: s.eyebrow })),
    { id: "deliverables", label: "Deliverables & stack" },
    ...(study.quote ? [{ id: "quote", label: "In their words" }] : []),
    { id: "next", label: "Work with us" },
  ];

  const sources = Array.from(
    new Set(study.project.metrics.map((m) => m.source).filter(Boolean)),
  );

  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        {/* Hero */}
        <section className="container-page pt-16 md:pt-24 pb-10">
          <nav className="text-xs uppercase tracking-widest text-muted-foreground">
            <Link to="/case-studies/" className="hover:text-primary">
              Case Studies
            </Link>{" "}
            <span className="opacity-40 mx-2" aria-hidden="true">/</span>
            <span className="text-foreground">{study.project.client}</span>
          </nav>

          <div className="mt-8 grid gap-10 lg:grid-cols-[1.2fr_1fr] items-end">
            <div>
              <EyebrowLabel>
                {study.hero.eyebrow} · {study.readingTime}
              </EyebrowLabel>
              <h1 className="mt-5 font-display text-4xl md:text-6xl leading-[1.02] text-balance">
                {study.hero.headline}
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
                {study.hero.deck}
              </p>
              <dl className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-border pt-6 text-sm">
                {[
                  ["Client", study.project.client],
                  ["Industry", study.project.industry],
                  ["Window", study.project.window],
                  ["Duration", study.project.duration],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                      {k}
                    </dt>
                    <dd className="mt-1 text-foreground">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="surface-card overflow-hidden">
              <Picture
                variantKey={study.project.coverVariant}
                alt={`${study.project.client} — ${sources.join(", ") || "results"} screenshot`}
                sizes="(min-width: 1024px) 50vw, 100vw"
                priority
                imgClassName="w-full h-auto object-cover object-top"
              />
              {sources.length > 0 && (
                <div className="p-3 text-[0.65rem] uppercase tracking-widest text-muted-foreground border-t border-border">
                  Source · {sources.join(" · ")}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* TOC + body */}
        <section className="container-page py-16 grid gap-12 lg:grid-cols-[220px_1fr_260px] items-start">
          <CaseStudyTOC items={tocItems} />

          <article className="max-w-3xl min-w-0">
            <section id="summary" className="scroll-mt-24">
              <EyebrowLabel>Executive summary</EyebrowLabel>
              <div className="mt-6 space-y-5">
                {study.executiveSummary.map((p, i) => (
                  <p
                    key={i}
                    className="text-lg leading-relaxed text-foreground/90"
                  >
                    {p}
                  </p>
                ))}
              </div>
            </section>

            <section id="results" className="scroll-mt-24 mt-16">
              <EyebrowLabel>Measured results</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl">
                Every number on this page is sourced.
              </h2>
              <p className="mt-3 text-sm text-muted-foreground">
                Metrics below are captured directly from{" "}
                {sources.join(", ") || "the client's analytics"} during the
                engagement window ({study.project.window}).
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {study.project.metrics.map((m) => (
                  <MetricCard key={m.label} metric={m} />
                ))}
              </div>
            </section>

            {study.sections.map((sec) => {
              const id = slugify(sec.heading);
              return (
                <section
                  key={id}
                  id={id}
                  className="scroll-mt-24 mt-16 border-t border-border pt-12"
                >
                  <EyebrowLabel>{sec.eyebrow}</EyebrowLabel>
                  <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight text-balance">
                    {sec.heading}
                  </h2>
                  <div className="mt-6 space-y-5">
                    {sec.body.map((p, j) => (
                      <p
                        key={j}
                        className="text-base md:text-lg leading-relaxed text-foreground/90"
                      >
                        {p}
                      </p>
                    ))}
                    {sec.bullets && (
                      <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                        {sec.bullets.map((b) => (
                          <li
                            key={b}
                            className="flex gap-3 rounded-lg bg-surface-2 p-4 text-sm text-foreground/90"
                          >
                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>
              );
            })}

            <section
              id="deliverables"
              className="scroll-mt-24 mt-16 border-t border-border pt-12"
            >
              <EyebrowLabel>What shipped</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl">Deliverables & stack</h2>
              <div className="mt-6 grid gap-8 md:grid-cols-2">
                <div>
                  <h3 className="text-xs uppercase tracking-widest text-primary">
                    Deliverables
                  </h3>
                  <ul className="mt-4 space-y-3">
                    {study.project.deliverables.map((d) => (
                      <li
                        key={d}
                        className="flex gap-3 text-sm text-foreground/90"
                      >
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-widest text-primary">
                    Tools & stack
                  </h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {study.project.stack.map((s) => (
                      <span
                        key={s}
                        className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {study.quote && (
              <section
                id="quote"
                className="scroll-mt-24 mt-16 border-t border-border pt-12"
              >
                <EyebrowLabel>In their words</EyebrowLabel>
                <blockquote className="mt-6 border-l-2 border-primary pl-6">
                  <p className="font-display text-2xl md:text-3xl leading-snug text-foreground text-balance">
                    "{study.quote.text}"
                  </p>
                  <footer className="mt-5 text-sm text-muted-foreground">
                    {study.quote.attribution} ·{" "}
                    <span className="text-foreground">{study.quote.role}</span>
                  </footer>
                </blockquote>
              </section>
            )}

            <section
              id="next"
              className="scroll-mt-24 mt-16 border-t border-border pt-12"
            >
              <div className="surface-card p-8 md:p-10">
                <EyebrowLabel>Work with us</EyebrowLabel>
                <h2 className="mt-3 font-display text-3xl">
                  Want results like this?
                </h2>
                <p className="mt-3 text-muted-foreground max-w-xl">
                  Book a free strategy call. We'll audit your organic footprint
                  and tell you honestly whether we're the right fit.
                </p>
                <Link
                  to="/contact/"
                  search={{ location: "", service: "case-study" }}
                  className="mt-6 inline-flex rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
                >
                  Book a strategy call →
                </Link>
              </div>
            </section>
          </article>

          {/* Outcome sidebar */}
          <aside className="hidden lg:block sticky top-24 space-y-4">
            <div className="surface-card p-5">
              <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                Outcome
              </div>
              <ul className="mt-4 space-y-3">
                {study.outcome.map((o) => (
                  <li key={o} className="flex items-start gap-3 text-sm">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 hairline pt-4 text-[0.7rem] text-muted-foreground space-y-1">
                <div>
                  Client{" "}
                  <span className="text-foreground">{study.project.client}</span>
                </div>
                <div>
                  Window{" "}
                  <span className="text-foreground">{study.project.window}</span>
                </div>
                <div>
                  Domain{" "}
                  <span className="text-foreground">{study.project.domain}</span>
                </div>
              </div>
            </div>
            <Link
              to="/portfolio/$slug/"
              params={{ slug: study.project.slug }}
              className="block surface-card p-5 hover:border-primary/50 transition-colors"
            >
              <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                Engagement summary
              </div>
              <div className="mt-2 text-sm text-foreground">
                See the shorter portfolio breakdown →
              </div>
            </Link>
          </aside>
        </section>

        {more.length > 0 && (
          <section className="container-page py-16 border-t border-border">
            <EyebrowLabel>Keep reading</EyebrowLabel>
            <h3 className="mt-3 font-display text-2xl md:text-3xl">
              More case studies
            </h3>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {more.map((c) => (
                <Link
                  key={c.slug}
                  to="/case-studies/$slug/"
                  params={{ slug: c.slug }}
                  className="surface-card p-6 hover:-translate-y-1 transition-transform"
                >
                  <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                    {c.hero.eyebrow} · {c.readingTime}
                  </div>
                  <h4 className="mt-3 font-display text-xl leading-tight">
                    {c.hero.headline}
                  </h4>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                    {c.hero.deck}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
